/**
 * Profile pictures and banners: where an upload goes, and which stored
 * references point at our own bucket (so replacing one may delete the old
 * object). Pure; the adapter does the I/O.
 *
 * Paths mirror the object policies in 20261001100000: the first segment is
 * the member's id, so a path built here is one the bucket lets them write.
 */

export const PROFILE_IMAGES_BUCKET = "profile-images";

export type ProfileImageKind = "avatar" | "banner";

const EXT: Record<string, string> = {
  "image/webp": "webp",
  "image/png": "png",
  "image/jpeg": "jpg",
};

/** `{userId}/{kind}-{uuid}.{ext}`. A fresh name per upload, so a CDN never serves the old picture under the new one's URL. */
export function profileImagePath(userId: string, kind: ProfileImageKind, mime: string, uuid: string): string {
  return `${userId}/${kind}-${uuid}.${EXT[mime] ?? "img"}`;
}

/**
 * The object path behind a stored avatar URL, when it is one of this member's
 * own uploads, else null. Avatar URLs can also be provider URLs (or someone
 * else's), and those must never be passed to remove().
 */
export function ownAvatarPath(avatarUrl: string | null, userId: string, publicBase: string): string | null {
  if (!avatarUrl || !avatarUrl.startsWith(publicBase)) return null;
  const path = decodeURIComponent(avatarUrl.slice(publicBase.length).split("?")[0]!);
  return isOwnPath(path, userId) ? path : null;
}

/** True for a path inside the member's own folder that does not climb out of it. */
export function isOwnPath(path: string | null | undefined, userId: string): path is string {
  return !!path && path.startsWith(`${userId}/`) && !path.includes("..");
}

/** Why a picture could not be set, for a message the member can act on. */
export class ProfileImageError extends Error {
  constructor(
    readonly reason: "too_large" | "bad_type" | "upload_failed",
    message: string,
  ) {
    super(message);
    this.name = "ProfileImageError";
  }
}

/** The i18n key for a failed picture change; anything unmodelled is a failed upload. */
export function profileImageErrorKey(e: unknown): string {
  const reason = e instanceof ProfileImageError ? e.reason : "upload_failed";
  return reason === "too_large"
    ? "profile.imageTooLarge"
    : reason === "bad_type"
      ? "profile.imageBadType"
      : "profile.imageUploadFailed";
}
